import { cn } from "@newxsystems/shared";
import { TootBadgeList } from "../../../../modules/content/components/TootBadgeList/TootBadgeList";
import type { TootBadgeListItem } from "../../../../modules/content/components/TootBadgeList/TootBadgeList.props";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import { TootTechPanel } from "../../../../modules/surface/components/TootTechPanel/TootTechPanel";
import type { BlockAction } from "@newxsystems/types";
import type { IResourceHighlightBlockProps } from "./ResourceHighlightBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

export const ResourceHighlightBlock = ({
  anchorId,
  buttons,
  category,
  downloadLabel,
  eyebrow,
  heading,
  image,
  link,
  parentClass,
  readingTime,
  resourceType,
  spacing,
  subheading,
  tags,
}: IResourceHighlightBlockProps) => {
  const metadata: TootBadgeListItem[] = [];
  if (resourceType) metadata.push({ label: "Resource type", value: resourceType });
  if (category) metadata.push({ label: "Category", value: category });
  if (readingTime) metadata.push({ label: "Reading time", value: readingTime });

  const primaryAction: BlockAction | undefined = link ? { ...link, style: "primary" } : undefined;
  const actionItems = [primaryAction, ...(buttons ?? [])].filter((action): action is BlockAction =>
    Boolean(action),
  );
  const imageSrc = image?.defaultSrc;
  const hasBody = Boolean(
    metadata.length || heading || subheading || downloadLabel || tags?.length || actionItems.length,
  );

  if (!heading && !hasBody && !image) {
    return null;
  }

  return (
    <TootSectionShell
      className="space-y-0"
      header={undefined}
      id={anchorId ?? undefined}
      rootClassName={cn("toot-resource-highlight-block", parentClass)}
      spacing={spacing ?? "loose"}
      tone="dark"
      width="wide"
    >
      <TootTechPanel className="scroll-mt-24" showDividers={false} variant="feature">
        <article
          className={cn(
            "grid min-w-0 overflow-hidden rounded-[var(--toot-radius-card)] border border-toot-grid/90 bg-toot-surface-raised/75 shadow-inner",
            imageSrc && "lg:grid-cols-[minmax(0,0.9fr)_minmax(22rem,1.1fr)]",
          )}
        >
          <div className="relative z-10 flex min-h-[27rem] flex-col gap-6 p-5 sm:p-6 md:p-8 lg:p-10">
            <div className="flex flex-wrap items-center gap-3">
              <TootBadgeList
                ariaLabel="Resource details"
                items={metadata}
                tone="accent"
                withAccessibleLabels
              />
              {downloadLabel ? (
                <span className="rounded-full border border-toot-grid/90 bg-toot-surface-raised px-3 py-1 text-xs font-semibold text-toot-muted">
                  {downloadLabel}
                </span>
              ) : null}
            </div>

            <div className="space-y-5">
              {eyebrow ? <p className="toot-eyebrow">{eyebrow}</p> : null}
              {heading ? (
                <h2 className="toot-heading-section max-w-[12ch] text-toot-foreground">
                  {heading}
                </h2>
              ) : null}
              {subheading ? (
                <RichTextHtml
                  className="max-w-[42rem] text-base leading-7 text-toot-muted [text-wrap:pretty] md:text-lg md:leading-8"
                  html={subheading}
                />
              ) : null}
            </div>

            {tags?.length ? (
              <TootBadgeList
                ariaLabel="Resource tags"
                className="max-w-xl"
                items={tags.map((tag) => ({ value: tag.tag }))}
                tone="muted"
              />
            ) : null}

            {actionItems.length > 0 ? (
              <TootActions actions={actionItems} alignment="left" className="mt-auto pt-2" />
            ) : null}
          </div>

          {imageSrc ? (
            <div className="relative overflow-hidden border-t border-toot-grid/90 bg-toot-surface-terminal/65 lg:border-l lg:border-t-0">
              <TootMediaFrame
                alt={image.alternateText}
                aspectRatio="auto"
                caption={image?.caption ?? undefined}
                className="h-full rounded-none border-0 bg-transparent [&>div>div]:h-full [&>div>div]:w-full [&>div>div]:p-0 [&>div]:h-full [&>div]:min-h-[23rem] [&>div]:bg-transparent"
                focalPoint={image?.focalPoint ?? undefined}
                height={image?.height ?? undefined}
                loading="eager"
                sizes={image?.sizes ?? "(min-width: 1024px) 48vw, 100vw"}
                src={imageSrc}
                srcSet={image?.srcSet ?? undefined}
                width={image?.width ?? undefined}
              />
            </div>
          ) : null}
        </article>
      </TootTechPanel>
    </TootSectionShell>
  );
};

ResourceHighlightBlock.displayName = "Block::ResourceHighlightBlock";
